import React, { useEffect, useState, useRef, Suspense } from "react";
import { Alert, Col, Row, Form, Button, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { Await, useRouteLoaderData } from "react-router-dom";
import { axiosInstance } from "../../auth/Auth";
import FreestyleDeltasTableDisplay from "./FreestyleDeltasTableDisplay";
import { convertMomentToDatepickerFormat } from "../HelperFunctions";
import moment from "moment/min/moment-with-locales";
import { DatePicker, DateRangePicker } from "rsuite";
import isAfter from "date-fns/isAfter";
import DaysPicker from "../Pickers/DaysPicker";
import { daysPerspectives, itemTypes } from "../constants";
import { toast } from "react-toastify";

export const FreestyleDeltasEditPage = ({ setOpenEditPage, editCollectionData, setEditCollectionData, handleDataRefresh, refreshKey }) => {
    const moduleName = "freestyleDeltasEdit";
    const { radios, userInfo } = useRouteLoaderData("root");
    const [freestyleDeltas, setFreestyleDeltas] = useState([]);
    const radiosTypeahead = useRef(null);
    const freestyleEditTypesTypehead = useRef(null);
    const collectionEditTypeahead = useRef(null);
    const [editCollection, setEditCollection] = useState([]);
    const [station, setStation] = useState([]);
    const [newCollectionName, setNewCollectionName] = useState("");
    const [dataLoading, setDataLoading] = useState(false);
    const [errorCode, setErrorCode] = useState(null);
    const [radioNames, setRadioNames] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [appendNewOption, setAppendNewOption] = useState(null);

    useEffect(() => {
        async function fetchData() {
            try {
                const { data } = await axiosInstance.get("/freestyle_deltas/current_user/");
                setFreestyleDeltas(data);
            } catch (error) {
                console.error("Error fetching freestyle deltas:", error);
            }
        }

        fetchData();
    }, [refreshKey]);

    const demo = [
        {
            demo_id: 1,
            name: "All",
            data_type: 1,
            listener_group: 0,
            data_demo_id: 0,
            ppm_area_id: null,
            music_test: true,
        },
    ];

    const [formData, setFormData] = useState({
        bit_name: "",
        date: moment().toDate(),
        start_time: "",
        end_time: "",
        duration: 0,
        itemTypes: itemTypes,
        days: daysPerspectives[0].days,
    });

    const [freestyleDeltasData, setFreestyleDeltasData] = useState(null);
    const [bitsInputSlider, setBitsInputSlider] = useState(1);

    useEffect(() => {
        if (editCollectionData) {
            setEditCollection(editCollectionData);
            getFreestyleDeltas(editCollectionData);
        }
    }, [editCollectionData]);

    useEffect(() => {
        if (editCollection && editCollection.length > 0) {
            setFreestyleDeltasData([]);
        }
    }, [editCollection]);

    useEffect(() => {
        async function getRadioNames() {
            const radiosData = (await radios).data;
            setRadioNames(
                radiosData.map(x => {
                    return { radio_id: x.radio_id, radio_name: x.radio_name };
                }),
            );
        }
        if (radios) {
            getRadioNames();
        }
    }, [radios]);

    const getRadioName = radio_id => radioNames.find(x => x.radio_id === radio_id)?.radio_name || "";

    function handleStationChange(value) {
        setStation(value);
    }

    function handleEditCollectionChange(value) {
        setEditCollection(value);
        if (value.length > 0) {
            setEditCollectionData(value);
            getFreestyleDeltas(value);
        }
    }

    const handleNewCollectionName = event => {
        setNewCollectionName(event.target.value);
    };

    const handleDurationChange = value => {
        if (value) {
            const selectedMoment = moment(value);
            const durationInSeconds = selectedMoment.minutes() * 60 + selectedMoment.seconds();
            setFormData({ ...formData, duration: durationInSeconds });
        } else {
            setFormData({ ...formData, duration: 0 });
        }
    };

    async function handleFormSubmit(event) {
        event.preventDefault();
        try {
            if (bitsInputSlider === 1) {
                await createSingleAdd();
            } else if (bitsInputSlider === 2) {
                await createTimeFrame();
            } else if (bitsInputSlider === 3) {
                await createMultipleItems();
            }
            toast.success(`New bit/s has been created.`, { autoClose: 5000 });
        } catch (error) {
            toast.error(error?.response?.data?.detail ?? "An unexpected error occurred");
            setErrorCode(error?.response?.status ?? 500);
        }
    }

    async function createSingleAdd() {
        const startDateTime = moment(formData.date).startOf("day");
        const [hours, minutes, seconds] = formData.start_time.split(":");
        startDateTime.add(hours, "hours").add(minutes, "minutes").add(seconds, "seconds");
        const endDateTime = startDateTime.clone().add(formData.duration, "seconds");

        const start_datetime = moment(startDateTime).format("YYYY-MM-DDTHH:mm:ss");
        const end_datetime = moment(endDateTime).format("YYYY-MM-DDTHH:mm:ss");

        try {
            const response = await axiosInstance.post(`/freestyle_deltas/${editCollection[0]?.collection_id}/bits/?analyze_now=true`, {
                bit_name: formData.bit_name,
                start_datetime: start_datetime,
                end_datetime: end_datetime,
            });
            setErrorCode(null);
            await getFreestyleDeltas(editCollection);
        } catch (error) {
            throw error;
        }
    }

    async function createTimeFrame() {
        const startTime = moment(formData.start_time, "HH:mm:ss");
        const endTime = startTime.clone().add(formData.duration, "seconds");
        const end_time = moment(endTime).format("HH:mm:ss");

        try {
            const response = await axiosInstance.post(
                `/freestyle_deltas/${editCollection[0]?.collection_id}/bits/batch?bit_name=${
                    formData.bit_name
                }&start_datetime=${dateRange[0].toISOString()}&end_datetime=${dateRange[1].toISOString()}&start_time=${
                    formData.start_time
                }&end_time=${end_time}&analyze_now=true`,
                formData.days,
            );
            setErrorCode(null);
            await getFreestyleDeltas(editCollection);
        } catch (error) {
            toast.error(error?.response?.data?.detail ?? "An unexpected error occurred");
            throw error;
        }
    }

    async function createMultipleItems() {
        const startDate = moment(dateRange[0]).startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const endDate = moment(dateRange[1]).endOf("day").format("YYYY-MM-DDTHH:mm:ss");

        const startTime = moment(formData.start_time, "HH:mm:ss");
        const endTime = startTime.clone().add(formData.duration, "seconds");
        const end_time = moment(endTime).format("HH:mm:ss");

        let itemTypesList = formData.itemTypes.map(x => x.id);
        try {
            const response = await axiosInstance.post(
                `/freestyle_deltas/${editCollection[0]?.collection_id}/bits/batch/item_type/?bit_name=${formData.bit_name}&start_date=${startDate}&end_date=${endDate}&start_time=${formData.start_time}&end_time=${end_time}&analyze_now=true`,
                { days: formData.days, item_types: itemTypesList },
            );
            setErrorCode(null);
            await getFreestyleDeltas(editCollection);
        } catch (error) {
            throw error;
        }
    }

    async function handleAddNewCollection(event) {
        event.preventDefault();
        setDataLoading(true);
        const resolvedUser = (await userInfo).data;
        try {
            const response = await axiosInstance.post("freestyle_deltas/", {
                radio_id: station[0]?.radio_id,
                user_id: resolvedUser.user_id,
                collection_name: newCollectionName ?? "",
            });
            const newCollection = response.data;
            setEditCollection([newCollection]);
            setNewCollectionName("");
            setStation([]);
            setAppendNewOption(response.data);
            toast.success(`New collection has been created.`, { autoClose: 5000 });
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
            toast.error(error?.response?.data?.detail ?? "An unexpected error occurred");
        }
        handleDataRefresh();
        setDataLoading(false);
    }

    async function handleSliderChange(value) {
        setBitsInputSlider(value);
    }

    async function getFreestyleDeltas(value) {
        setDataLoading(true);
        try {
            const response = await axiosInstance.get("freestyle_deltas/" + value[0]?.collection_id + "/bits/", {
                collection_id: value[0]?.collection_id,
                demo_id: 1,
                listener_group: 1,
            });
            setFreestyleDeltasData(response.data);
            setErrorCode(null);
        } catch (error) {
            setFreestyleDeltasData(null);
            setErrorCode(error?.response?.status ?? 500);
        }

        setDataLoading(false);
    }

    return (
        <>
            <Row>
                <Col className="bg-white px-4 pt-4">
                    <h1 className="text-uppercase">Freestyle Deltas</h1>
                    <div class="alert alert-primary" role="alert">
                        Dear RadioAnalyzers, with the most recent batch of updates Content Testing has now completely replaced and improved on the functionality
                        of Freestyle Deltas. As a consequence, it will be phased out in May. If you have any questions reach out to us at{" "}
                        <a href="mailto:support@radioanalyzer.com" className="alert-link">
                            support@radioanalyzer.com
                        </a>
                    </div>
                    <Col xs={12} xl={12} className="text-center text-xl-start align-self-end mb-4">
                        <Button
                            onClick={() => {
                                setOpenEditPage(false);
                                handleDataRefresh();
                            }}
                            className="btn-ra-yellow text-uppercase px-4 py-1 mb-xl-1">
                            Analyze Results
                        </Button>
                    </Col>
                    <h3>Add new collection</h3>
                    <Form onSubmit={handleAddNewCollection} className="row my-3">
                        <Col xs={12} xl={8}>
                            <Row>
                                <Suspense fallback={<span>Loading...</span>}>
                                    <Await resolve={radios}>
                                        {currentRadios => (
                                            <Form.Group controlId="freestyle-deltas-radio-selection" as={Col} className="mb-3 mb-xl-0 col-12 col-xl-4">
                                                <Form.Label>Radio</Form.Label>
                                                <Typeahead
                                                    id="freestyle-deltas-radio--selection"
                                                    ref={radiosTypeahead}
                                                    options={currentRadios.data.filter(x => x.mtt_only === false && (x.stream === true || x.ppm === true))}
                                                    labelKey={"radio_name"}
                                                    highlightOnlyResult
                                                    placeholder="Select radio..."
                                                    clearButton={true}
                                                    onChange={handleStationChange}
                                                    selected={station}
                                                />
                                            </Form.Group>
                                        )}
                                    </Await>
                                </Suspense>
                                <Form.Group controlId="freestyle-deltas-new-collection-name" as={Col} className="mb-3 mb-xl-0 col-12 col-xl-6">
                                    <Form.Label>New collection name</Form.Label>
                                    <Form.Control
                                        value={newCollectionName}
                                        onChange={handleNewCollectionName}
                                        aria-label="Default"
                                        aria-describedby="inputGroup-sizing-default"
                                    />
                                </Form.Group>
                            </Row>
                        </Col>
                        <Col xs={12} xl={4} className="text-center text-xl-end align-self-end">
                            <Button type="submit" className="btn-ra-yellow text-uppercase px-5 p-1" disabled={!station.length || !newCollectionName}>
                                Setup new collection
                            </Button>
                        </Col>
                    </Form>
                </Col>
            </Row>
            <Row>
                <Col className="bg-white p-4">
                    <h3>Edit collection</h3>
                    <Col xs={12} xl={8}>
                        <Row>
                            <Form.Group controlId="freestyle-deltas-edit-collection" as={Col} className="mb-3 mb-xl-0 col-12 col-xl-4">
                                <Typeahead
                                    id="freestyle-deltas-collection-selection"
                                    ref={collectionEditTypeahead}
                                    options={freestyleDeltas}
                                    labelKey={option => `${option.collection_name} | ${getRadioName(option.radio_id)}`}
                                    highlightOnlyResult
                                    placeholder="Select collection..."
                                    clearButton={true}
                                    onChange={handleEditCollectionChange}
                                    selected={editCollection}
                                />
                            </Form.Group>
                        </Row>
                    </Col>
                </Col>
            </Row>
            <Row>
                <Col className="bg-white px-4 pb-4">
                    <Col xs={12} xl={6}>
                        <ToggleButtonGroup
                            type="radio"
                            name="bits-slider"
                            value={bitsInputSlider}
                            defaultChecked={1}
                            onChange={handleSliderChange}
                            className="ra-switcher">
                            <ToggleButton className="text-nowrap" id={`bits-slider-single`} variant="ra-dark-blue-outline" value={1}>
                                Single Add
                            </ToggleButton>
                            <ToggleButton className="text-nowrap" id={`bits-slider-timeframe`} variant="ra-dark-blue-outline" value={2}>
                                Timeframe
                            </ToggleButton>
                            <ToggleButton className="text-nowrap" id={`bits-slider-items`} variant="ra-dark-blue-outline" value={3}>
                                Items
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Col>
                </Col>
            </Row>
            <Row>
                <Col className="bg-white px-4 pb-4">
                    <Form onSubmit={handleFormSubmit} className="row bg-white ">
                        <Col xs={12} xl={12}>
                            <Row>
                                <Form.Group controlId="freestyle-deltas-new-bit-name" as={Col} className="mb-3  col-12 col-xl-3">
                                    <Form.Label>Bit name</Form.Label>
                                    <Form.Control
                                        value={formData.bit_name}
                                        onChange={e => setFormData({ ...formData, bit_name: e.currentTarget.value })}
                                        placeholder="Bit name"
                                        disabled={!editCollection?.length}
                                        aria-label="Default"
                                        aria-describedby="inputGroup-sizing-default"
                                    />
                                </Form.Group>
                                {bitsInputSlider === 1 ? (
                                    <Form.Group controlId="freestyle-deltas-new-bit-date" as={Col} className="mb-3 col-12 col-xl-2">
                                        <Form.Label>Date</Form.Label>
                                        <DatePicker
                                            oneTap
                                            value={formData.date}
                                            onChange={value => setFormData({ ...formData, date: value })}
                                            format={convertMomentToDatepickerFormat(moment.localeData(moment.locale()).longDateFormat("l"))}
                                            placeholder="Select a day"
                                            shouldDisableDate={date => isAfter(date, moment().toDate())}
                                            block
                                            showonecalendar={true}
                                            disabled={!editCollection?.length}
                                        />
                                    </Form.Group>
                                ) : (
                                    <Form.Group controlId="freestyle-deltas-new-bit-daterange" as={Col} className="mb-3 col-12 col-xl-3">
                                        <Form.Label>Time period</Form.Label>
                                        <DateRangePicker
                                            value={dateRange}
                                            onChange={value => setDateRange(value)}
                                            placeholder="Select time period"
                                            format={convertMomentToDatepickerFormat(moment.localeData(moment.locale()).longDateFormat("l"))}
                                            ranges={[
                                                {
                                                    label: "Last 24 hours",
                                                    value: [moment().clone().subtract(1, "days").startOf("day").toDate(), moment().toDate()],
                                                    placement: "left",
                                                },
                                                {
                                                    label: "Latest week",
                                                    value: [moment().clone().subtract(1, "week").toDate(), moment().toDate()],
                                                    placement: "left",
                                                },
                                                {
                                                    label: "Latest 2 weeks",
                                                    value: [moment().clone().subtract(2, "weeks").toDate(), moment().toDate()],
                                                    placement: "left",
                                                },
                                                {
                                                    label: "Latest month",
                                                    value: [moment().clone().subtract(1, "month").toDate(), moment().toDate()],
                                                    placement: "left",
                                                },
                                                {
                                                    label: "Latest quarter",
                                                    value: [moment().clone().subtract(3, "months").toDate(), moment().toDate()],
                                                    placement: "left",
                                                },
                                            ]}
                                            shouldDisableDate={date => isAfter(date, moment().toDate())}
                                            block
                                            showonecalendar={true}
                                            disabled={!editCollection?.length}
                                        />
                                    </Form.Group>
                                )}

                                <Form.Group as={Col} className="mb-3 col-12 col-xl-2">
                                    <Form.Label>Start Time</Form.Label>
                                    <Form.Control
                                        type={"time"}
                                        step="1"
                                        placeholder={"Start time"}
                                        defaultValue={formData.start_time}
                                        disabled={!editCollection?.length}
                                        onChange={e => setFormData({ ...formData, start_time: e.currentTarget.value })}
                                    />
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3 col-12 col-xl-2">
                                    <Form.Label>Duration</Form.Label>
                                    <DatePicker
                                        format="mm:ss"
                                        defaultValue={new Date(2000, 0, 1, 0, 0, 0)}
                                        editable={true}
                                        onChange={handleDurationChange}
                                        className="w-100"
                                        disabled={!editCollection?.length}
                                    />
                                </Form.Group>
                                {bitsInputSlider !== 1 && (
                                    <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} disabled={!editCollection?.length} />
                                )}
                                {bitsInputSlider === 3 ? (
                                    <Form.Group as={Col} className="col-12 col-xl-6">
                                        <Form.Label>Item Types</Form.Label>
                                        <Typeahead
                                            id="freestyle-deltas-new-bit--item-types"
                                            options={itemTypes}
                                            ref={freestyleEditTypesTypehead}
                                            labelKey={"label"}
                                            multiple
                                            clearButton
                                            disabled={!editCollection?.length}
                                            selected={formData.itemTypes}
                                            onChange={value => {
                                                setFormData({ ...formData, itemTypes: value });
                                                freestyleEditTypesTypehead.current.toggleMenu();
                                            }}
                                        />
                                    </Form.Group>
                                ) : (
                                    ""
                                )}
                                {bitsInputSlider === 1 ? (
                                    <Col xs={12} xl={3} className="text-center text-xl-end align-self-end mb-3">
                                        <Button
                                            type="submit"
                                            className="btn-ra-dark-blue text-uppercase px-5 p-1"
                                            disabled={
                                                !editCollection ||
                                                !formData.start_time ||
                                                !formData.bit_name ||
                                                !formData.date ||
                                                !formData.duration ||
                                                formData.duration === 0
                                            }>
                                            Add bit
                                        </Button>
                                    </Col>
                                ) : (
                                    <Col xs={12} xl={12} className="text-center text-xl-end align-self-end mt-3">
                                        <Button
                                            type="submit"
                                            className="btn-ra-dark-blue text-uppercase px-5 p-1"
                                            disabled={
                                                !editCollection ||
                                                !formData.start_time ||
                                                !formData.bit_name ||
                                                !dateRange ||
                                                !formData.duration ||
                                                formData.duration === 0 ||
                                                !formData.days ||
                                                (bitsInputSlider === 3 && (!formData.itemTypes || formData.itemTypes.length === 0))
                                            }>
                                            Add bits
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Form>
                </Col>
            </Row>

            <Row>
                <Col className="bg-white px-4">
                    <h4>Added bits to {editCollection?.[0]?.collection_name ?? ""}</h4>
                    <FreestyleDeltasTableDisplay
                        freestyleDeltasData={freestyleDeltasData}
                        setFreestyleDeltasData={setFreestyleDeltasData}
                        demo={demo}
                        collection={editCollection}
                        showStack={false}
                    />
                </Col>
            </Row>
        </>
    );
};
